import clsx from 'clsx';
import Button, {SIZES} from 'components/Button';
import {ChangeEvent, FC} from 'react';
import {c} from 'ttag';

import {ScaleType} from '../types';

type ControlPanelProps = {
    numPages: number;
    pageNumber: number;
    scale: number;
    setPageNumber: (pageNumber: number) => void;
    setScale: (scale: ScaleType) => void;
};

const ControlPanel: FC<ControlPanelProps> = ({
    numPages,
    pageNumber,
    scale,
    setPageNumber,
    setScale,
}) => {
    const isFirstPage = pageNumber === 1;
    const isLastPage = pageNumber === numPages;

    const goToPreviousPage = () => {
        if (!isFirstPage) setPageNumber(pageNumber - 1);
    };

    const goToNextPage = () => {
        if (!isLastPage) setPageNumber(pageNumber + 1);
    };

    const onPageChange = (event: ChangeEvent<HTMLInputElement>) => {
        setPageNumber(Number(event.target.value));
    };

    const isMinZoom = scale <= 0.5;
    const isMaxZoom = scale >= 2;

    const changeScale = (offset: number) => {
        setScale(scale + offset);
    };

    const zoomOut = () => {
        changeScale(-0.1);
    };

    const zoomIn = () => {
        changeScale(0.1);
    };

    const onSelectionChange = (event: ChangeEvent<HTMLSelectElement>) => {
        const {value} = event.target;

        if (value === 'automatic') {
            return setScale('automatic');
        }

        if (value === 'actual' || value === '1') {
            return setScale(1);
        }

        if (value === '0.75' || value === '0.5') {
            return setScale(Number(value));
        }

        throw new Error(`UNKNOWN VALUE: ${value}`);
    };

    return (
        <div className="mb-2 flex justify-between border border-grey-300 bg-grey-100 p-2">
            <div className="mx-3 flex">
                <Button
                    disabled={isFirstPage}
                    icon="small_arrow_up"
                    kind="tertiary"
                    onClick={goToPreviousPage}
                    size="xs"
                />
                <div className="mx-1 inline-block h-6 w-px justify-center rounded border border-grey-300 align-middle" />
                <Button
                    disabled={isLastPage}
                    icon="small_arrow_down"
                    kind="tertiary"
                    onClick={goToNextPage}
                    size="xs"
                />
                <div className="mx-3">
                    <input
                        className={clsx('w-12 p-0 pl-1', SIZES.xs)}
                        key={numPages}
                        max={numPages || 1}
                        min={1}
                        name="pageNumber"
                        onChange={onPageChange}
                        type="text"
                        value={pageNumber}
                    />
                    {c('component').t` of ${numPages}`}
                </div>
            </div>
            <div className="mx-3 flex">
                <Button
                    disabled={isMinZoom}
                    icon="minus"
                    kind="tertiary"
                    onClick={zoomOut}
                    size="xs"
                />
                <div className="mx-1 inline-block h-6 w-px justify-center rounded border border-grey-300 align-middle" />
                <Button
                    disabled={isMaxZoom}
                    icon="plus"
                    kind="tertiary"
                    onClick={zoomIn}
                    size="xs"
                />
                <select
                    className={clsx('mx-3 w-40 p-0 pl-1', SIZES.xs)}
                    onChange={onSelectionChange}
                >
                    <option selected={true} value="automatic">
                        {c('component').t`Automatic Zoom`}
                    </option>
                    <option value="actual">{c('component')
                        .t`Actual Size`}</option>
                    <option value="1">100%</option>
                    <option value="0.75">75%</option>
                    <option value="0.5">50%</option>
                </select>
            </div>
        </div>
    );
};

export default ControlPanel;
